import { formatDate, InputSize, InputText } from '@ui/src';
import { Controller } from 'react-hook-form';
import { CustomerAdditionalInformationContentTypes } from '../types';
import AdditionalInfoBlock from './additional-info-block';
import AmlGeneralInfoRow from './general-info-row';
import { CustomerStatus } from '../../../../hoc/common/types';
import Question from './question';
import RegulatedCompanyRow from './corporate-questionnarie/row';
import ContactPerson from './corporate-questionnarie/contact-person';
import Pep from './corporate-questionnarie/pep';
import GroupOfCompanies from './corporate-questionnarie/group-of-companies';
import BusinessActivities from './corporate-questionnarie/business-activities';
import CustomersWealth from './corporate-questionnarie/customers-wealth';
import AccountsCurrencies from './corporate-questionnarie/accounts-currencies';
import AccountsFlows from './corporate-questionnarie/accounts-flows';
import ExpectedOriginFunds from './corporate-questionnarie/expected-origin-funds';

import cn from '../styles.module.css';

const CorporateAdditionalInformation = ({
  currentApplicationScheme,
  control,
  questionnaire,
  status,
  selectedFilter,
  setSelectedFilter,
  questionnaireFields,
  setValue,
  errors,
  clearErrors,
  relationshipToTheCustomerDictionary,
  countries,
  sourceOfWealth,
  baseCurrency,
  businessActivities,
  customerMainClients,
  salesChannels,
  customerSourceOfFunds,
  customerSourceOfWealth,
  annualIncomes,
  totalIncomeError,
  setTotalIncomeError,
  typeOfAccounts,
  accountsCurrenciesIsLoading,
  accountsCurrenciesIsFetching,
  accountsCurrenciesValues,
  setAccountsCurrenciesValues,
  currencies,
  onDeleteAccountCurrencies,
  selectedAccountsCurrencyId,
  deleteAccountsCurrenciesInProgress,
  setAccountsFlowsValues,
  accountsFlowsValues,
  accountsFlowsIsLoading,
  accountsFlowsIsFetching,
  currenciesList,
  expectedFundsOriginsIsLoading,
  expectedFundsOriginsIsFetching,
  deleteExpectedFundsOriginsInProgress,
  onDeleteExpectedFundsOrigins,
  selectedExpectedFundsOriginsId,
  expectedFundsOriginsValues,
  setExpectedFundsOriginsValues,
}: CustomerAdditionalInformationContentTypes) => (
  <>
    {currentApplicationScheme?.id ===
      'cdbb8c71-90e6-4a5e-964f-e55169e0c4df' && (
      <>
        <AdditionalInfoBlock title="" withoutTitle>
          <RegulatedCompanyRow title="Trading name (optional)">
            <Controller
              control={control}
              name="tradingName"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter trading name..."
                  value={value}
                  size={InputSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['tradingName']);
                  }}
                  className="borderless"
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.tradingName?.message}
                />
              )}
            />
          </RegulatedCompanyRow>
          <RegulatedCompanyRow title="Legal Entity Identifier Code (optional)">
            <Controller
              control={control}
              name="legalEntityIdCode"
              defaultValue=""
              render={({ field: { onChange, value, name } }) => (
                <InputText
                  name={name}
                  placeholder="Enter code..."
                  value={value}
                  onChange={(e) => {
                    onChange(e);
                    clearErrors(['legalEntityIdCode']);
                  }}
                  size={InputSize.SMALL}
                  className="borderless"
                  disabled={status === CustomerStatus.CLOSED}
                  tabIndex={status === CustomerStatus.CLOSED ? -1 : 0}
                  error={errors?.legalEntityIdCode?.message}
                />
              )}
            />
          </RegulatedCompanyRow>
        </AdditionalInfoBlock>

        <ContactPerson
          control={control}
          status={status}
          errors={errors}
          clearErrors={clearErrors}
        />
        <Pep
          control={control}
          status={status}
          setValue={setValue}
          errors={errors}
          clearErrors={clearErrors}
          fields={questionnaireFields}
          relationshipToTheCustomerDictionary={
            relationshipToTheCustomerDictionary
          }
          countries={countries}
        />
        <GroupOfCompanies
          control={control}
          status={status}
          setValue={setValue}
          errors={errors}
          clearErrors={clearErrors}
          fields={questionnaireFields}
          countries={countries}
          sourceOfWealth={sourceOfWealth}
          baseCurrency={baseCurrency}
        />
        <BusinessActivities
          control={control}
          status={status}
          setValue={setValue}
          errors={errors}
          clearErrors={clearErrors}
          fields={questionnaireFields}
          businessActivities={businessActivities}
          customerMainClients={customerMainClients}
          salesChannels={salesChannels}
          customerSourceOfFunds={customerSourceOfFunds}
        />
        <CustomersWealth
          control={control}
          status={status}
          setValue={setValue}
          errors={errors}
          clearErrors={clearErrors}
          fields={questionnaireFields}
          customerSourceOfWealth={customerSourceOfWealth}
          baseCurrency={baseCurrency}
          annualIncomes={annualIncomes}
          totalIncomeError={totalIncomeError}
          setTotalIncomeError={setTotalIncomeError}
        />
        <AccountsCurrencies
          control={control}
          status={status}
          errors={errors}
          clearErrors={clearErrors}
          typeOfAccounts={typeOfAccounts}
          accountsCurrenciesIsLoading={accountsCurrenciesIsLoading}
          accountsCurrenciesIsFetching={accountsCurrenciesIsFetching}
          accountsCurrenciesValues={accountsCurrenciesValues}
          setAccountsCurrenciesValues={setAccountsCurrenciesValues}
          currencies={currencies}
          onDeleteAccountCurrencies={onDeleteAccountCurrencies}
          selectedAccountsCurrencyId={selectedAccountsCurrencyId}
          deleteAccountsCurrenciesInProgress={
            deleteAccountsCurrenciesInProgress
          }
          setAccountsFlowsValues={setAccountsFlowsValues}
          accountsFlowsValues={accountsFlowsValues}
        />
        <AccountsFlows
          accountsFlowsValues={accountsFlowsValues}
          accountsFlowsIsLoading={accountsFlowsIsLoading}
          accountsFlowsIsFetching={accountsFlowsIsFetching}
          errors={errors}
          setAccountsFlowsValues={setAccountsFlowsValues}
          currenciesList={currenciesList}
          status={status}
        />
        <ExpectedOriginFunds
          expectedFundsOriginsIsLoading={expectedFundsOriginsIsLoading}
          expectedFundsOriginsIsFetching={expectedFundsOriginsIsFetching}
          accountsFlowsValues={accountsFlowsValues}
          errors={errors}
          status={status}
          accountsCurrenciesValues={accountsCurrenciesValues}
          currencies={currencies}
          deleteExpectedFundsOriginsInProgress={
            deleteExpectedFundsOriginsInProgress
          }
          onDeleteExpectedFundsOrigins={onDeleteExpectedFundsOrigins}
          selectedExpectedFundsOriginsId={selectedExpectedFundsOriginsId}
          expectedFundsOriginsValues={expectedFundsOriginsValues}
          setExpectedFundsOriginsValues={setExpectedFundsOriginsValues}
          countries={countries}
        />
      </>
    )}
    {(currentApplicationScheme?.typeOfBusinessLicenceActive ||
      currentApplicationScheme?.hmrcMlrNumberActive ||
      currentApplicationScheme?.hmrcRegistrationExpiryDateActive ||
      currentApplicationScheme?.fcaNumberActive ||
      currentApplicationScheme?.icoNumberActive) && (
      <AdditionalInfoBlock title="Licenses and registration">
        <>
          {currentApplicationScheme?.typeOfBusinessLicenceActive && (
            <AmlGeneralInfoRow
              title="Type of business licence? (API, SPI, EMI, OTHER)"
              control={control}
              className={cn.additionalInfoRow}
              name="typeOfBusinessLicence"
              type="text"
              value={questionnaire?.typeOfBusinessLicence}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
          {currentApplicationScheme?.hmrcMlrNumberActive && (
            <AmlGeneralInfoRow
              title="HMRC MLR number"
              control={control}
              className={cn.additionalInfoRow}
              name="hmrcMlrNumber"
              type="text"
              value={questionnaire?.hmrcMlrNumber}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
          {currentApplicationScheme?.hmrcRegistrationExpiryDateActive && (
            <AmlGeneralInfoRow
              title="HMRC expiry date"
              className={cn.additionalInfoRow}
              control={control}
              name="hmrcRegistrationExpiryDate"
              type="date"
              value={
                questionnaire?.hmrcRegistrationExpiryDate
                  ? formatDate(questionnaire?.hmrcRegistrationExpiryDate)
                  : ''
              }
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
          {currentApplicationScheme?.fcaNumberActive && (
            <AmlGeneralInfoRow
              title="FCA number"
              className={cn.additionalInfoRow}
              control={control}
              name="fcaNumber"
              type="text"
              value={questionnaire?.fcaNumber}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
          {currentApplicationScheme?.icoNumberActive && (
            <AmlGeneralInfoRow
              title="ICO number"
              className={cn.additionalInfoRow}
              control={control}
              name="icoNumber"
              type="text"
              value={questionnaire?.icoNumber}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        </>
      </AdditionalInfoBlock>
    )}
    {(currentApplicationScheme?.numberOfBranchesActive ||
      currentApplicationScheme?.numberOfAgentsActive) && (
      <AdditionalInfoBlock title="Business Structure">
        <>
          {currentApplicationScheme?.numberOfBranchesActive && (
            <AmlGeneralInfoRow
              title="Number of branches"
              className={cn.additionalInfoRow}
              control={control}
              name="numberOfBranches"
              type="text"
              value={questionnaire?.numberOfBranches}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
          {currentApplicationScheme?.numberOfAgentsActive && (
            <AmlGeneralInfoRow
              title="Number of agents"
              className={cn.additionalInfoRow}
              control={control}
              name="numberOfAgents"
              type="text"
              value={questionnaire?.numberOfAgents}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        </>
      </AdditionalInfoBlock>
    )}
    {(currentApplicationScheme?.mlroDetailsActive ||
      currentApplicationScheme?.complianceConsultantDetailsActive ||
      currentApplicationScheme?.accountantDetailsActive ||
      currentApplicationScheme?.itProviderDetailsActive ||
      currentApplicationScheme?.payoutPartnerDetailsActive) && (
      <AdditionalInfoBlock title="Key contacts">
        <>
          {currentApplicationScheme?.mlroDetailsActive && (
            <AmlGeneralInfoRow
              title="MLRO details"
              className={cn.additionalInfoRow}
              control={control}
              name="mlroDetails"
              type="textarea"
              value={questionnaire?.mlroDetails}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
          {currentApplicationScheme?.complianceConsultantDetailsActive && (
            <AmlGeneralInfoRow
              title="Compliance consultant details"
              className={cn.additionalInfoRow}
              control={control}
              name="complianceConsultantDetails"
              type="textarea"
              value={questionnaire?.complianceConsultantDetails}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
          {currentApplicationScheme?.accountantDetailsActive && (
            <AmlGeneralInfoRow
              title="Accountant details"
              className={cn.additionalInfoRow}
              control={control}
              name="accountantDetails"
              type="textarea"
              value={questionnaire?.accountantDetails}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
          {currentApplicationScheme?.itProviderDetailsActive && (
            <AmlGeneralInfoRow
              title="IT provider details"
              className={cn.additionalInfoRow}
              control={control}
              name="itProviderDetails"
              type="textarea"
              value={questionnaire?.itProviderDetails}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
          {currentApplicationScheme?.payoutPartnerDetailsActive && (
            <AmlGeneralInfoRow
              title="Pay out partner details"
              className={cn.additionalInfoRow}
              control={control}
              name="payoutPartnerDetails"
              type="textarea"
              value={questionnaire?.payoutPartnerDetails}
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        </>
      </AdditionalInfoBlock>
    )}
    {currentApplicationScheme?.id !==
      'cdbb8c71-90e6-4a5e-964f-e55169e0c4df' && (
      <>
        <div className={cn.checklistTitle}>Checklist</div>
        <div className={cn.filters}>
          <button
            type="button"
            className={`${cn.filterButton} ${
              selectedFilter === 'all' ? cn.active : ''
            }`}
            onClick={() => setSelectedFilter('all')}
          >
            Show all
          </button>
          <button
            type="button"
            className={`${cn.filterButton} ${
              selectedFilter === 'onlyYes' ? cn.active : ''
            }`}
            onClick={() => setSelectedFilter('onlyYes')}
          >
            Only “Yes”
          </button>
        </div>
      </>
    )}

    <div className={cn.checklistContent}>
      {currentApplicationScheme?.transactionMonitoringActive && (
        <div>
          {(selectedFilter === 'all' ||
            (selectedFilter === 'onlyYes' &&
              Boolean(
                questionnaireFields?.hasTransactionMonitoring === 'yes',
              ))) && (
            <Question
              question="Do you carry out transaction monitoring?"
              control={control}
              name="hasTransactionMonitoring"
              active={Boolean(
                questionnaireFields?.hasTransactionMonitoring === 'yes',
              )}
              answer={
                questionnaire?.hasTransactionMonitoring === 'yes'
                  ? `Yes${
                      questionnaire?.transactionMonitoringKind === 'AUTO'
                        ? ', Automated'
                        : ', Manual'
                    }`
                  : 'No'
              }
              additionalName="transactionMonitoringKind"
              additionalList={[
                {
                  name: 'Automated',
                  value: 'AUTO',
                },
                {
                  name: 'Manual',
                  value: 'MANUAL',
                },
              ]}
              setValue={setValue}
              additionalQuestion="Is your transaction monitoring"
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        </div>
      )}
      {currentApplicationScheme?.screeningActive && (
        <div>
          {(selectedFilter === 'all' ||
            (selectedFilter === 'onlyYes' &&
              Boolean(questionnaireFields?.hasScreening === 'yes'))) && (
            <Question
              question="Do you carry out sanction screening, adverse media screening, and PEP screening?"
              control={control}
              name="hasScreening"
              active={Boolean(questionnaireFields?.hasScreening === 'yes')}
              additionalName="screeningKind"
              answer={
                questionnaire?.hasScreening === 'yes'
                  ? `Yes  ${
                      questionnaire?.screeningKind === 'AUTO'
                        ? ', Automated'
                        : ', Manual'
                    }`
                  : 'No'
              }
              additionalList={[
                {
                  name: 'Automated',
                  value: 'AUTO',
                },
                {
                  name: 'Manual',
                  value: 'MANUAL',
                },
              ]}
              setValue={setValue}
              additionalQuestion="Is your screening"
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        </div>
      )}
      {currentApplicationScheme?.regulatoryInvestigationActive && (
        <div>
          {(selectedFilter === 'all' ||
            (selectedFilter === 'onlyYes' &&
              Boolean(
                questionnaireFields?.hasRegulatoryInvestigation === 'yes',
              ))) && (
            <Question
              question="Have you been under any regulatory investigation in the last 24 months?"
              control={control}
              name="hasRegulatoryInvestigation"
              active={Boolean(
                questionnaireFields?.hasRegulatoryInvestigation === 'yes',
              )}
              answer={
                questionnaire?.hasRegulatoryInvestigation === 'yes'
                  ? 'Yes'
                  : 'No'
              }
              additionalName="regulatoryInvestigationDetails"
              additionalQuestion="Provide details"
              details={questionnaire?.regulatoryInvestigationDetails}
              setValue={setValue}
              textarea
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        </div>
      )}
      {currentApplicationScheme?.visitedByAuthoritiesActive && (
        <div>
          {(selectedFilter === 'all' ||
            (selectedFilter === 'onlyYes' &&
              Boolean(questionnaireFields?.visitedByAuthorities))) && (
            <Question
              question="Have you been visited by any of the following authorities in the last 24 months?"
              control={control}
              name="visitedByAuthorities"
              answer={questionnaire?.visitedByAuthorities}
              details={questionnaire?.visitedByAuthoritiesDetails}
              mainList={[
                {
                  name: 'HMRC',
                  value: 'HMRC',
                },
                {
                  name: 'FCA',
                  value: 'FCA',
                },
                {
                  name: 'NCA',
                  value: 'NCA',
                },
                {
                  name: 'Metropolitan Police',
                  value: 'METROPOLITAN_POLICE',
                },
                {
                  name: 'No',
                  value: 'NO',
                },
              ]}
              active={Boolean(questionnaireFields?.visitedByAuthorities)}
              additionalName="visitedByAuthoritiesDetails"
              additionalQuestion="Provide details"
              setValue={setValue}
              textarea
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        </div>
      )}
      {currentApplicationScheme?.onboardCorporateClientsActive && (
        <div>
          {(selectedFilter === 'all' ||
            (selectedFilter === 'onlyYes' &&
              Boolean(
                questionnaireFields?.hasOnboardCorporateClients === 'yes',
              ))) && (
            <Question
              question="Do you onboard corporate clients?"
              control={control}
              name="hasOnboardCorporateClients"
              active={Boolean(
                questionnaireFields?.hasOnboardCorporateClients === 'yes',
              )}
              answer={
                questionnaire?.hasOnboardCorporateClients === 'yes'
                  ? 'Yes'
                  : 'No'
              }
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        </div>
      )}
      {currentApplicationScheme?.onboardMsbActive && (
        <div>
          {selectedFilter === 'all' ||
            (selectedFilter === 'onlyYes' &&
              Boolean(questionnaireFields?.hasOnboardMsb === 'yes') && (
                <Question
                  question="Do you onboard Money Service Businesses (MSBs)?"
                  answer={questionnaire?.hasOnboardMsb === 'yes' ? 'Yes' : 'No'}
                  active={Boolean(questionnaireFields?.hasOnboardMsb === 'yes')}
                  control={control}
                  name="hasOnboardMsb"
                  disabled={status === CustomerStatus.CLOSED}
                />
              ))}
        </div>
      )}

      {currentApplicationScheme?.paymentsForCharitiesActive && (
        <div>
          {(selectedFilter === 'all' ||
            (selectedFilter === 'onlyYes' &&
              Boolean(
                questionnaireFields?.hasPaymentsForCharities === 'yes',
              ))) && (
            <Question
              question="Do you make payments for charities?"
              name="hasPaymentsForCharities"
              control={control}
              active={Boolean(
                questionnaireFields?.hasPaymentsForCharities === 'yes',
              )}
              answer={
                questionnaire?.hasPaymentsForCharities === 'yes' ? 'Yes' : 'No'
              }
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        </div>
      )}
      {currentApplicationScheme?.changesInManagementOrOwnershipActive && (
        <div>
          {(selectedFilter === 'all' ||
            (selectedFilter === 'onlyYes' &&
              Boolean(
                questionnaireFields?.hasChangesInManagementOrOwnership ===
                'yes',
              ))) && (
            <Question
              question="Is the company undergoing any changes in management or ownership?"
              control={control}
              name="hasChangesInManagementOrOwnership"
              active={Boolean(
                questionnaireFields?.hasChangesInManagementOrOwnership ===
                'yes',
              )}
              answer={
                questionnaire?.hasChangesInManagementOrOwnership === 'yes'
                  ? 'Yes'
                  : 'No'
              }
              additionalName="changesInManagementOrOwnershipDetails"
              additionalQuestion="Provide details"
              setValue={setValue}
              details={questionnaire?.changesInManagementOrOwnershipDetails}
              textarea
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        </div>
      )}
      {currentApplicationScheme?.workingWithAnyOtherIPSPActive && (
        <div>
          {(selectedFilter === 'all' ||
            (selectedFilter === 'onlyYes' &&
              Boolean(
                questionnaireFields?.hasWorkingWithAnyOtherIpsp === 'yes',
              ))) && (
            <Question
              question="Are you currently working with any other IPSP (Intermediary Payment Service Provider)?"
              control={control}
              name="hasWorkingWithAnyOtherIpsp"
              active={Boolean(
                questionnaireFields?.hasWorkingWithAnyOtherIpsp === 'yes',
              )}
              additionalName="workingWithAnyOtherIpspDetails"
              additionalQuestion="Provide details"
              answer={
                questionnaire?.hasWorkingWithAnyOtherIpsp === 'yes'
                  ? 'Yes'
                  : 'No'
              }
              setValue={setValue}
              details={questionnaire?.workingWithAnyOtherIpspDetails}
              textarea
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        </div>
      )}
      {currentApplicationScheme?.auditedByExternalComplianceActive && (
        <div>
          {(selectedFilter === 'all' ||
            (selectedFilter === 'onlyYes' &&
              Boolean(
                questionnaireFields?.hasAuditedByExternalCompliance === 'yes',
              ))) && (
            <Question
              question="Has the institution been audited by external compliance auditors in the past 12 months?"
              control={control}
              name="hasAuditedByExternalCompliance"
              active={Boolean(
                questionnaireFields?.hasAuditedByExternalCompliance === 'yes',
              )}
              answer={
                questionnaire?.hasAuditedByExternalCompliance === 'yes'
                  ? 'Yes'
                  : 'No'
              }
              additionalName="auditedByExternalComplianceDetails"
              additionalQuestion="Provide details"
              details={questionnaire?.auditedByExternalComplianceDetails}
              setValue={setValue}
              textarea
              disabled={status === CustomerStatus.CLOSED}
            />
          )}
        </div>
      )}
    </div>
  </>
);

export default CorporateAdditionalInformation;
